import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { Schemas } from '../types/api-types';
import apiClient from '../clients/apiClient';
import { useAuth } from './AuthProvider';

export type UserPermission = Schemas['UserPermissionResponse'];
export type UserMenu = Schemas['UserMenuResponse'];

interface UserPermissionAndMenuContextType {
    userPermissions: UserPermission[] | null;
    userMenus: UserMenu[] | null;
    userFavoriteMenus: UserMenu[] | null;
    loading: boolean;
    error: string | null;
    fetchUserPermissions: () => Promise<void>;
    fetchUserMenus: () => Promise<void>;
    fetchUserFavouriteMenus: () => Promise<void>;
    hasPermission: (permissionId: string) => boolean;
    hasMenu: (menuId: number) => boolean;
    getAccessibleRoutes: () => string[];
    setRefreshTrigger: (refresh: boolean) => void;
}

const UserPermissionAndMenuContext = createContext<UserPermissionAndMenuContextType | undefined>(undefined);

export const UserPermissionAndMenuProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [userPermissions, setUserPermissions] = useState<UserPermission[] | null>(null);
    const [userMenus, setUserMenus] = useState<UserMenu[] | null>(null);
    const [userFavoriteMenus, setUserFavoriteMenus] = useState<UserMenu[] | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { isAuthenticated } = useAuth();
    const [refreshTrigger, setRefreshTrigger] = useState(false);

    const fetchUserPermissions = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiClient.get(`/users/permission_list`);
            setUserPermissions(response.data.data || []);
        } catch (err: any) {
            const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to fetch user permissions';
            setError(errorMessage);
            setUserPermissions(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchUserMenus = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiClient.get(`/users/menu_list`);
            setUserMenus(response.data.data || []);
        } catch (err: any) {
            const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to fetch user menus';
            setError(errorMessage);
            setUserMenus(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchUserFavouriteMenus = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await apiClient.get(`/users/favourite_menu_list`);
            setUserFavoriteMenus(response.data.data || []);
        } catch (err: any) {
            const errorMessage = err?.response?.data?.detail || err?.message || 'Failed to fetch user menus';
            setError(errorMessage);
            setUserFavoriteMenus(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const hasPermission = useCallback((permissionId: string): boolean => {
        if (!userPermissions) return false;
        return userPermissions.some(p => p.permission === permissionId);
    }, [userPermissions]);

    const hasMenu = useCallback((menuId: number): boolean => {
        if (!userMenus) return false;
        return userMenus.some(m => m.id === menuId && m.has_menu === true);
    }, [userMenus]);

    const getAccessibleRoutes = useCallback((): string[] => {
        if (!userMenus) return [];
        // Get all menu routes that have has_menu = true (are actually accessible routes)
        return userMenus
            .filter(m => m.has_menu === true && m.to_url)
            .map(m => m.to_url!)
            .filter((url, index, self) => self.indexOf(url) === index); // Remove duplicates
    }, [userMenus]);

    // Fetch permissions and menus when user changes
    useEffect(() => {
        if (!isAuthenticated) {
            return;
        }
        fetchUserPermissions();
        fetchUserMenus();
        fetchUserFavouriteMenus();
    }, [fetchUserPermissions, fetchUserMenus, fetchUserFavouriteMenus, isAuthenticated]);

    useEffect(() => {
        if (refreshTrigger) {
            fetchUserPermissions();
            fetchUserMenus();
            fetchUserFavouriteMenus();
            setRefreshTrigger(false);
        }
    }, [refreshTrigger]);

    return (
        <UserPermissionAndMenuContext.Provider
            value={{
                userPermissions,
                userMenus,
                userFavoriteMenus,
                loading,
                error,
                fetchUserPermissions,
                fetchUserMenus,
                fetchUserFavouriteMenus,
                hasPermission,
                hasMenu,
                getAccessibleRoutes,
                setRefreshTrigger,
            }}
        >
            {children}
        </UserPermissionAndMenuContext.Provider>
    );
};

export const useUserPermissionAndMenu = (): UserPermissionAndMenuContextType => {
    const context = useContext(UserPermissionAndMenuContext);
    if (!context) {
        throw new Error('useUserPermissionAndMenu must be used within UserPermissionAndMenuProvider');
    }
    return context;
};
